import { useBookerStore } from "@calcom/features/bookings/Booker/store";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { showToast } from "@calcom/ui/components/toast";
import { useCallback, useState } from "react";

export type UseVerifySmsReturnType = ReturnType<typeof useVerifySms>;

export interface IUseVerifySmsProps {
  phoneNumber: string;
  onVerified?: () => void;
}

export const useVerifySms = ({ phoneNumber, onVerified }: IUseVerifySmsProps) => {
  const [isSmsVerificationModalVisible, setSmsVerificationModalVisible] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState("");
  const [verifiedPhone, setVerifiedPhone] = useState<string | null>(null);
  const isRescheduling = useBookerStore((state) => Boolean(state.rescheduleUid && state.bookingData));

  const { t, i18n } = useLocale();

  const handleSendSmsCode = useCallback(async () => {
    if (!phoneNumber) return;

    setIsSending(true);
    try {
      const response = await fetch("/api/auth/send-sms-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phoneNumber,
          language: i18n.language || "tr",
        }),
      });

      if (response.ok) {
        setSmsVerificationModalVisible(true);
        showToast(t("sms_sent") || "SMS gönderildi", "success");
      } else if (response.status === 429) {
        showToast(t("rate_limit_exceeded") || "Çok fazla istek. Lütfen daha sonra tekrar deneyin.", "error");
      } else {
        showToast(t("sms_not_sent") || "SMS gönderilemedi", "error");
      }
    } catch {
      showToast(t("sms_not_sent") || "SMS gönderilemedi", "error");
    } finally {
      setIsSending(false);
    }
  }, [phoneNumber, i18n.language, t]);

  const handleVerifyCode = useCallback(
    async (code: string, phone: string) => {
      try {
        const response = await fetch("/api/auth/verify-sms-code", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phoneNumber: phone, code }),
        });

        const data = await response.json();
        setIsPending(false);

        if (response.ok && data.verified) {
          setVerifiedPhone(phone);
          setSmsVerificationModalVisible(false);
          onVerified?.();
        } else {
          setError(t("code_provided_invalid") || "Geçersiz doğrulama kodu");
        }
      } catch {
        setIsPending(false);
        setError(t("something_went_wrong") || "Bir hata oluştu");
      }
    },
    [t, onVerified]
  );

  // SMS verification is always required when phone number is present (unless rescheduling or already verified)
  const shouldSkipSmsVerification =
    isRescheduling || !phoneNumber || (phoneNumber && verifiedPhone === phoneNumber);

  return {
    handleSendSmsCode,
    handleVerifyCode,
    isSmsVerificationModalVisible,
    setSmsVerificationModalVisible,
    shouldSkipSmsVerification: Boolean(shouldSkipSmsVerification),
    isSending,
    isPending,
    setIsPending,
    error,
    resetErrors: () => setError(""),
    verifiedPhone,
  };
};
